import {
  Button,
  DeactivatableButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormDateTimeInput,
  FormSelectInput,
} from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useStopDateError } from '@folio/utils/useStopDateError';
import {
  getBusinessRoleId,
  getStopDatePickerProps,
  hasNoStopDateInput,
  isStopReasonAllowedTo,
  isStopReasonAvailable,
} from '@folio/utils/stop-reasons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  policyControllerStopPolicy,
} from 'korint-sdk';
import { useFeatureFlag } from 'korint-sdk/react';
import { HandIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { StopConfirmation } from './StopConfirmation';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Text } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

// funnelBasePath opts an app in; without it the flag alone cannot route to a URL that does not exist.
export function TerminationButton({ funnelBasePath }: { funnelBasePath?: string } = {}) {
  // An app that never routed the funnel does not read the flag at all.
  return funnelBasePath ? (
    <FunnelOrDialogButton funnelBasePath={funnelBasePath} />
  ) : (
    <TerminationDialogButton />
  );
}

function FunnelOrDialogButton({ funnelBasePath }: { funnelBasePath: string }) {
  const { policy } = usePolicyOnDashboard();
  const funnelEnabled = useFeatureFlag('ops_policy_stop_branch', policy.productId);

  return funnelEnabled ? (
    <StopFunnelButton funnelBasePath={funnelBasePath} />
  ) : (
    <TerminationDialogButton />
  );
}

function StopFunnelButton({ funnelBasePath }: { funnelBasePath: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { policyId } = useAssertedParams();

  return (
    <DeactivatableButton
      variant="outline"
      leftIcon={<HandIcon className="h-4 w-4" />}
      rules={[]}
      onClick={() => navigate(`${funnelBasePath}/${policyId}/stop/reason`)}
    >
      {t('terminationButton.tooltip')}
    </DeactivatableButton>
  );
}

function TerminationDialogButton() {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { policyId } = useAssertedParams();

  const { policy, mutate } = usePolicyOnDashboard();
  const { t } = useTranslation();

  const { data: user } = useSuspenseQuery(legacyPermissionsControllerAtMeQueryOptions());
  const businessRoleId = getBusinessRoleId({ user, policy });

  const { data: stopReasons = [] } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: ({ policyStopReasons }) =>
      policyStopReasons?.filter(reason => isStopReasonAllowedTo({ reason, businessRoleId })) ?? [],
  });

  const reasonValues = useMemo(() => stopReasons.map(({ reason }) => reason), [stopReasons]);

  // A full-refund reason makes the backend stop the policy at its start date, ignoring any
  // chosen stop date — so the date picker is hidden and "now" is sent instead.
  const fullRefundReasons = useMemo(
    () => new Set(stopReasons.filter(({ refundType }) => refundType === 'FULL').map(r => r.reason)),
    [stopReasons],
  );

  // A reason with a requestNoticePeriod but no appliedAtDate takes no stopDate input at all — the
  // date picker is hidden and "now" is sent, since the backend computes the effective date itself.
  const hasNoStopDateReasons = useMemo(
    () => new Set(stopReasons.filter(hasNoStopDateInput).map(r => r.reason)),
    [stopReasons],
  );

  // A reason whose validity window or notice-period deadline has elapsed, or whose notice-period
  // anchor the policy cannot supply, is shown disabled — the backend would reject it.
  const unavailableReasons = useMemo(
    () =>
      new Set(
        stopReasons
          .filter(
            reason =>
              !isStopReasonAvailable({
                reason,
                policy: {
                  startedAt: policy.startedAt,
                  nextRenewalAt: policy.nextRenewalAt,
                  suspendedAt: policy.suspendedAt,
                },
              }),
          )
          .map(r => r.reason),
      ),
    [stopReasons, policy.startedAt, policy.nextRenewalAt, policy.suspendedAt],
  );

  const stopReasonOptions = useMemo(
    () =>
      reasonValues.map(value => ({
        label: t([`stopReasons.${value}`, value]),
        value,
        disabled: unavailableReasons.has(value),
      })),
    [reasonValues, unavailableReasons, t],
  );

  const stopDateError = useStopDateError(policy);

  const schema = z
    .object({
      reason:
        reasonValues.length > 0
          ? z.enum(reasonValues as [string, ...string[]], {
              required_error: t('terminationButton.validation.reasonRequired'),
            })
          : z.string().refine(val => reasonValues.includes(val), {
              message: t('terminationButton.validation.reasonRequired'),
            }),
      stopDate: z.string().optional(),
    })
    .refine(
      ({ reason, stopDate }) =>
        fullRefundReasons.has(reason) || hasNoStopDateReasons.has(reason) || !!stopDate,
      {
        path: ['stopDate'],
        message: t('terminationButton.validation.stopDateRequired'),
      },
    )
    .superRefine(({ reason, stopDate }, context) => {
      const message = stopDateError({
        reason: stopReasons.find(({ reason: configReason }) => configReason === reason),
        stopDate,
      });
      if (message) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['stopDate'], message });
      }
    });

  type TerminationFormValues = z.infer<typeof schema>;

  const form = useForm<TerminationFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    shouldUnregister: true,
  });

  const selectedReason = form.watch('reason');
  const isFullRefund = fullRefundReasons.has(selectedReason);
  const hasNoStopDate = hasNoStopDateReasons.has(selectedReason);
  const selectedReasonConfig = stopReasons.find(({ reason }) => reason === selectedReason);

  const { mutateAsync: stopPolicy } = useMutation({
    mutationFn: async (data: TerminationFormValues) => {
      const stopDate =
        fullRefundReasons.has(data.reason) ||
        hasNoStopDateReasons.has(data.reason) ||
        !data.stopDate
          ? new Date().toISOString()
          : data.stopDate;
      return await policyControllerStopPolicy(policyId, {
        reason: data.reason,
        details: [{ key: 'stopDate', value: stopDate }],
      });
    },
    onSuccess: async () => {
      await mutate();
      setConfirming(false);
      setOpen(false);
    },
  });

  return (
    <Form {...form}>
      <Dialog
        open={open}
        onOpenChange={isOpen => {
          setOpen(isOpen);
          setConfirming(false);
        }}
      >
        <DialogTrigger asChild>
          <DeactivatableButton
            variant="outline"
            leftIcon={<HandIcon className="h-4 w-4" />}
            rules={[]}
          >
            {t('terminationButton.tooltip')}
          </DeactivatableButton>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('terminationButton.dialog.title')}</DialogTitle>
          </DialogHeader>
          <Stack asChild>
            <form
              id="termination-form"
              onSubmit={form.handleSubmit(async values => {
                await stopPolicy(values);
              })}
              className={confirming ? 'hidden' : undefined}
            >
              <FormSelectInput
                label={t('terminationButton.dialog.reasonLabel')}
                placeholder={t('terminationButton.dialog.reasonPlaceholder')}
                name="reason"
                required
                control={form.control}
                options={stopReasonOptions}
              />
              {isFullRefund ? (
                <Text size="sm" tone="muted">
                  {t('terminationButton.dialog.fullRefundNotice')}
                </Text>
              ) : (
                !hasNoStopDate && (
                  <FormDateTimeInput
                    control={form.control}
                    name="stopDate"
                    label={t('terminationButton.dialog.stopDateLabel')}
                    placeholder={t('terminationButton.dialog.stopDatePlaceholder')}
                    required
                    {...getStopDatePickerProps({ reason: selectedReasonConfig, policy })}
                  />
                )
              )}
            </form>
          </Stack>
          {confirming ? (
            <StopConfirmation
              formId="termination-form"
              reasonLabel={t([`stopReasons.${selectedReason}`, selectedReason])}
              stopDate={isFullRefund || hasNoStopDate ? undefined : form.watch('stopDate')}
              loading={form.formState.isSubmitting}
              onBack={() => setConfirming(false)}
            />
          ) : (
            <DialogFooter>
              <Button type="button" onClick={async () => setConfirming(await form.trigger())}>
                {t('terminationButton.dialog.submit')}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </Form>
  );
}
